import { io, Socket } from 'socket.io-client';
import { SOCKET_URL } from '../../utils/constants';
import { getToken } from '../../utils/auth/tokenStorage';
import { logger } from '../../utils/logger';

// Singleton socket instance
let socket: Socket | null = null;

// Reconnection attempt count
let reconnectionAttempts = 0;
const MAX_RECONNECTION_ATTEMPTS = 5;

// Critical events that require special logging and handling
const CRITICAL_EVENTS = [
  'video:forceLeave',
  'videoMeeting:forceLeave', // Add both possible event names
  'video:feedbackSubmitted',
  'video:yourSessionEnded',
  'waitingRoom:currentParticipantCleared',
  'waitingRoom:participantFeedbackCompleted',
  'waitingRoom:participantCompleted',
  'waitingRoom:redirectToDashboard' // Add redirect event
];

/**
 * Initialize Socket.IO client with proper error handling
 */
export const initializeSocket = (): Socket | null => {
  try {
    if (socket) return socket;
    
    const token = getToken();
    
    if (!token) {
      logger.warn('No authentication token available for socket connection');
      return null;
    }
    
    console.log('Initializing Socket.IO connection to:', SOCKET_URL);
    
    // Create a new socket
    const newSocket = io(SOCKET_URL, {
      auth: { token },
      reconnection: true,
      reconnectionAttempts: MAX_RECONNECTION_ATTEMPTS,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
      transports: ['websocket', 'polling'],
    });
    
    // Connection events
    newSocket.on('connect', () => {
      console.log('Socket.IO connected successfully');
      logger.info('Socket connected');
      logger.info('Socket connection established');
      reconnectionAttempts = 0;
    });
    
    newSocket.on('disconnect', (reason) => {
      console.log(`Socket.IO disconnected: ${reason}`);
      logger.warn(`Socket disconnected: ${reason}`);
    });
    
    newSocket.on('connect_error', (error) => {
      console.error(`Socket.IO connection error: ${error.message}`);
      logger.error(`Socket connection error: ${error.message}`);
      
      reconnectionAttempts++;
      if (reconnectionAttempts >= MAX_RECONNECTION_ATTEMPTS) {
        logger.error(`Max reconnection attempts (${MAX_RECONNECTION_ATTEMPTS}) reached, giving up`);
      }
    });
    
    // Add special handling for critical events - with improved logging
    CRITICAL_EVENTS.forEach(eventName => {
      newSocket.on(eventName, (data) => {
        // Log in a consistent format for easier debugging
        const dataStr = typeof data === 'object' ? JSON.stringify(data) : data;
        console.log(`CRITICAL EVENT RECEIVED: ${eventName}`, data);
        logger.info(`CRITICAL EVENT RECEIVED: ${eventName} - ${dataStr}`);
        
        // Emit a special global event for critical events that might need immediate action
        if (typeof window !== 'undefined') {
          try {
            const customEvent = new CustomEvent('socket:critical', { 
              detail: { eventName, data }
            });
            window.dispatchEvent(customEvent);
          } catch (e) {
            // Silently fail if custom event can't be created
          }
        }
      });
    });
    
    // Debug events being received
    newSocket.onAny((event, ...args) => {
      console.log(`Socket event received: ${event}`, args);
    });
    
    // Assign to global socket variable
    socket = newSocket;
    
    return socket;
  } catch (error: any) {
    console.error('Socket initialization error:', error.message);
    logger.error(`Socket initialization error: ${error.message}`);
    return null;
  }
};

/**
 * Get the socket instance
 */
export const getSocket = (): Socket | null => {
  // Try to initialize if not already done
  if (!socket) {
    return initializeSocket();
  }
  return socket;
};

/**
 * Disconnect the socket
 */
export const disconnectSocket = (): void => {
  if (socket) {
    socket.disconnect();
    socket = null;
    logger.info('Socket disconnected manually');
  }
};

/**
 * Update socket authentication
 */
export const updateSocketAuth = (token: string | null): void => {
  if (!token) {
    disconnectSocket();
    return;
  }
  
  if (socket) {
    // Update auth token
    socket.auth = { token };
    
    // If socket is connected, disconnect and reconnect with new token
    if (socket.connected) {
      logger.info('Updating socket authentication token');
      
      try {
        // Create a new socket with the updated token
        // First disconnect existing connection
        socket.disconnect();
        
        // Then create a completely new socket
        const newSocket = io(SOCKET_URL, {
          auth: { token },
          reconnection: true,
          reconnectionAttempts: MAX_RECONNECTION_ATTEMPTS,
          reconnectionDelay: 1000,
          reconnectionDelayMax: 5000,
          timeout: 20000,
          transports: ['websocket', 'polling'],
        });
        
        // Set up event handlers on the new socket
        newSocket.on('connect', () => {
          console.log('Socket.IO reconnected with new token');
          logger.info('Socket reconnected with new token');
          reconnectionAttempts = 0;
        });
        
        newSocket.on('disconnect', (reason) => {
          console.log(`Socket.IO disconnected: ${reason}`);
          logger.warn(`Socket disconnected: ${reason}`);
        });
        
        newSocket.on('connect_error', (error) => {
          console.error(`Socket.IO connection error: ${error.message}`);
          logger.error(`Socket connection error: ${error.message}`);
          
          reconnectionAttempts++;
          if (reconnectionAttempts >= MAX_RECONNECTION_ATTEMPTS) {
            logger.error(`Max reconnection attempts (${MAX_RECONNECTION_ATTEMPTS}) reached, giving up`);
          }
        });
        
        // Set up critical event handlers with improved logging
        CRITICAL_EVENTS.forEach(eventName => {
          newSocket.on(eventName, (data) => {
            // Log in a consistent format for easier debugging
            const dataStr = typeof data === 'object' ? JSON.stringify(data) : data;
            console.log(`CRITICAL EVENT RECEIVED: ${eventName}`, data);
            logger.info(`CRITICAL EVENT RECEIVED: ${eventName} - ${dataStr}`);
            
            // Emit a special global event for critical events that might need immediate action
            if (typeof window !== 'undefined') {
              try {
                const customEvent = new CustomEvent('socket:critical', { 
                  detail: { eventName, data }
                });
                window.dispatchEvent(customEvent);
              } catch (e) {
                // Silently fail if custom event can't be created
              }
            }
          });
        });
        
        // Set up debug event handler
        newSocket.onAny((event, ...args) => {
          console.log(`Socket event received: ${event}`, args);
        });
        
        // Update the global socket reference
        socket = newSocket;
      } catch (error) {
        logger.error(`Error during socket reconnection: ${error}`);
        // Reset socket and try to initialize again
        socket = null;
        initializeSocket();
      }
    }
  } else {
    // Initialize socket with new token
    initializeSocket();
  }
};

/**
 * Emit an event through the socket
 */
export const emitEvent = (event: string, data: any): void => {
  const socketInstance = getSocket();
  if (socketInstance && socketInstance.connected) {
    console.log(`Emitting socket event: ${event}`, data);
    socketInstance.emit(event, data);
  } else {
    console.warn(`Cannot emit event ${event}: Socket not connected`);
  }
};

/**
 * Join specified rooms to ensure proper event delivery
 */
export const ensureRoomMembership = (meetingId: string, userId: string): void => {
  const socketInstance = getSocket();
  if (socketInstance && socketInstance.connected) {
    console.log(`Joining critical rooms for meeting: ${meetingId}, user: ${userId}`);
    
    // Join all possible rooms we might receive events on
    socketInstance.emit('meeting:join', { meetingId });
    socketInstance.emit('video:participantJoined', { meetingId, userId });
    
    // Add redundant room joining for safety
    socketInstance.emit('join:room', { room: `meeting:${meetingId}` });
    socketInstance.emit('join:room', { room: `video:${meetingId}` });
    
    // Add explicit room joining for participant-specific rooms
    socketInstance.emit('join:room', { room: `meeting:${meetingId}:user:${userId}` });
    socketInstance.emit('join:room', { room: `video:${meetingId}:participant:${userId}` });
  }
};

export default {
  initializeSocket,
  getSocket,
  disconnectSocket,
  updateSocketAuth,
  emitEvent,
  ensureRoomMembership
};
